// Google Analytics 4 wrapper functions
import { logger } from './logger';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export const initGA4 = (measurementId: string) => {
  if (typeof window === 'undefined') return;
  
  // GA is already initialized in index.html, but we ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];
  
  // If gtag doesn't exist, initialize it (fallback)
  if (!window.gtag) {
    window.gtag = function(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
  }
  
  // Update config to disable automatic page views (we track manually)
  // This ensures we have consistent tracking even if gtag was already loaded
  if (window.gtag) {
    window.gtag('config', measurementId, {
      send_page_view: false, // We'll track page views manually with events for better control
    });
  }
};

export const trackEvent = (eventName: string, parameters?: Record<string, unknown>) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', eventName, parameters);
};

export const trackPageView = (path: string, title?: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  // Use page_view event instead of config to avoid resetting client ID
  // This prevents duplicate user counts
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
  });
};

export const setUserProperties = (userId: string, properties: Record<string, unknown>) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  // Set user ID in GA4 - this is critical for accurate user tracking
  const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID || 'G-ZZC0H6XJXS';
  window.gtag('config', measurementId, {
    user_id: userId,
  });
  
  // Set user properties
  window.gtag('set', 'user_properties', {
    user_id: userId,
    ...properties,
  });
};

export const clearUser = () => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  // Clear user ID when user signs out
  const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID || 'G-ZZC0H6XJXS';
  window.gtag('config', measurementId, {
    user_id: null,
  });
};

// Track specific events
export const trackSignUp = (method: string, role: string, signup_source?: string) => {
  trackEvent('sign_up', { method, user_role: role, signup_source: signup_source || undefined });
};

export const trackLogin = (method: string, role: string) => {
  trackEvent('login', { method, user_role: role });
};

/** Track signup form start (for funnel analytics) */
export const trackSignupAttempt = (source: string, role?: string) => {
  trackEvent('signup_start', { source, role });
};

/** Track waitlist form submission */
export const trackWaitlistSubmit = (source: string, role: string, referralSource?: string) => {
  trackEvent('waitlist_submit', {
    signup_source: source,
    role,
    referral_source: referralSource,
  });
};

export const trackRFQCreation = (category: string, quantity: number, value?: number) => {
  trackEvent('create_rfq', { category, quantity, value });
};

export const trackQuoteSubmission = (rfqId: string, value: number) => {
  trackEvent('submit_quote', { rfq_id: rfqId, value });
};

export const trackOrderPlacement = (orderId: string, value: number, productName: string) => {
  trackEvent('purchase', {
    transaction_id: orderId,
    value,
    currency: 'USD',
    items: [{ item_id: orderId, item_name: productName }],
  });
};

export const trackProductView = (productId: string, productName: string, price: number) => {
  trackEvent('view_item', {
    items: [{
      item_id: productId,
      item_name: productName,
      price,
    }],
  });
};

export const trackSearch = (searchTerm: string, resultsCount: number) => {
  trackEvent('search', { search_term: searchTerm, results_count: resultsCount });
};

export const trackMessageSend = (conversationId: string) => {
  trackEvent('send_message', { conversation_id: conversationId });
};

export const trackProfileUpdate = (userId: string) => {
  trackEvent('profile_update', { user_id: userId });
};

/**
 * Core Web Vitals Monitoring
 * Tracks LCP, FID, and CLS for performance monitoring
 */

interface WebVitalMetric {
  name: string;
  value: number;
  id: string;
  delta: number;
  rating: 'good' | 'needs-improvement' | 'poor';
}

/**
 * Initialize Core Web Vitals tracking
 * Tracks LCP (Largest Contentful Paint), FID (First Input Delay), and CLS (Cumulative Layout Shift)
 * 
 * Performance budgets:
 * - LCP: < 2.5s (good), 2.5s-4s (needs improvement), > 4s (poor)
 * - FID: < 100ms (good), 100ms-300ms (needs improvement), > 300ms (poor)
 * - CLS: < 0.1 (good), 0.1-0.25 (needs improvement), > 0.25 (poor)
 */
export const initWebVitals = () => {
  if (typeof window === 'undefined') return;

  // Use dynamic import to avoid build-time errors if package is missing
  Promise.resolve().then(async () => {
    try {
      // Dynamic import - package is installed, so this should work
      const webVitals = await import('web-vitals');
      
      if (!webVitals) return;

      const { onLCP, onFID, onCLS, onFCP, onTTFB } = webVitals;

      // Largest Contentful Paint
      onLCP((metric: WebVitalMetric) => {
        trackEvent('web_vital', {
          metric_name: 'LCP',
          value: metric.value,
          rating: metric.rating,
          delta: metric.delta,
        });
        
        // Log to console in development
        if (import.meta.env.DEV) {
          logger.debug('LCP metric', { value: metric.value, rating: metric.rating });
        }
      });

      // First Input Delay
      onFID((metric: WebVitalMetric) => {
        trackEvent('web_vital', {
          metric_name: 'FID',
          value: metric.value,
          rating: metric.rating,
          delta: metric.delta,
        });
        
        if (import.meta.env.DEV) {
          logger.debug('FID metric', { value: metric.value, rating: metric.rating });
        }
      });

      // Cumulative Layout Shift
      onCLS((metric: WebVitalMetric) => {
        trackEvent('web_vital', {
          metric_name: 'CLS',
          value: metric.value,
          rating: metric.rating,
          delta: metric.delta,
        });
        
        if (import.meta.env.DEV) {
          logger.debug('CLS metric', { value: metric.value, rating: metric.rating });
        }
      });

      // First Contentful Paint (bonus metric)
      onFCP((metric: WebVitalMetric) => {
        trackEvent('web_vital', {
          metric_name: 'FCP',
          value: metric.value,
          rating: metric.rating,
          delta: metric.delta,
        });
      });

      // Time to First Byte (bonus metric)
      onTTFB((metric: WebVitalMetric) => {
        trackEvent('web_vital', {
          metric_name: 'TTFB',
          value: metric.value,
          rating: metric.rating,
          delta: metric.delta,
        });
      });
    } catch (error) {
      // Silently fail if web-vitals is not available or fails to load
      // Package is installed, so this should rarely happen
      // No need to log - web-vitals is optional and failures are expected in some environments
    }
  });
};
